import React from 'react';
import { colors, availableEquipment, wardCategories } from './constants';
import { XIcon, WrenchIcon, PlusIcon, TrashIcon, EditIcon, InfoIcon, BuildingIcon } from './Icons';

const BedManagementModal = ({ 
  show, 
  onClose,
  bedManagementMode,
  setBedManagementMode,
  currentWard,
  stats,
  beds,
  setBeds,
  newBedData,
  setNewBedData,
  handleAddNewBed,
  handleRemoveBed,
  handleNewBedEquipmentToggle,
  setSelectedBedForEdit,
  setEditBedData,
  setShowEditBedModal,
  showNotification,
  allWards,
  newWardData,
  setNewWardData,
  handleCreateWard,
  handleWardFacilityToggle
}) => {
  if (!show) return null;

  const wardTypes = [
    { value: 'general', label: 'General Ward', description: 'Basic shared accommodation' },
    { value: 'semi_private', label: 'Semi-Private Ward', description: '2-4 beds per room' },
    { value: 'private', label: 'Private Ward', description: 'Single occupancy room' },
    { value: 'deluxe', label: 'Deluxe Ward', description: 'Premium single room with amenities' },
    { value: 'super_deluxe', label: 'Super Deluxe Ward', description: 'Luxury accommodation' },
    { value: 'suite', label: 'Suite', description: 'Multiple rooms with living area' },
    { value: 'icu', label: 'ICU', description: 'Intensive Care Unit' },
    { value: 'iccu', label: 'ICCU', description: 'Intensive Cardiac Care Unit' },
    { value: 'nicu', label: 'NICU', description: 'Neonatal Intensive Care Unit' },
    { value: 'picu', label: 'PICU', description: 'Pediatric Intensive Care Unit' },
    { value: 'hdu', label: 'HDU', description: 'High Dependency Unit' },
    { value: 'emergency', label: 'Emergency Ward', description: 'Acute care and stabilization' }
  ];

  const departmentOptions = [
    'General Medicine',
    'General Surgery',
    'Cardiology',
    'Neurology',
    'Orthopedics',
    'Pediatrics',
    'Obstetrics & Gynecology',
    'Oncology',
    'Psychiatry',
    'Nephrology',
    'Pulmonology',
    'Gastroenterology',
    'Urology',
    'ENT',
    'Ophthalmology',
    'Dermatology',
    'Critical Care',
    'Emergency Medicine'
  ];

  const wardFacilities = [
    'Attached Bathroom',
    'Air Conditioning',
    'TV',
    'WiFi',
    'Refrigerator',
    'Microwave',
    'Sofa/Couch',
    'Attendant Bed',
    'Medical Gas Outlets',
    'Nurse Call System',
    'Dietary Services',
    'Laundry Services'
  ];

  const styles = {
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      backdropFilter: 'blur(4px)'
    },
    modal: {
      background: colors.cream100,
      borderRadius: '12px',
      width: '90%',
      maxWidth: '900px',
      height: '85vh',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
    },
    modalHeader: {
      padding: '24px',
      borderBottom: `1px solid rgba(94, 82, 64, 0.12)`,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      background: colors.cream100,
      borderRadius: '12px 12px 0 0'
    },
    modalTitle: {
      fontSize: '18px',
      fontWeight: 550,
      color: colors.slate900,
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    closeButton: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '8px',
      display: 'flex',
      alignItems: 'center',
      color: colors.slate500,
      borderRadius: '6px',
      transition: 'all 0.2s ease'
    },
    tabContainer: {
      display: 'flex',
      borderBottom: `1px solid rgba(94, 82, 64, 0.12)`,
      background: colors.cream100
    },
    tab: {
      flex: 1,
      padding: '16px',
      fontSize: '14px',
      fontWeight: 500,
      border: 'none',
      background: 'transparent',
      color: colors.slate500,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      borderBottom: '2px solid transparent'
    },
    tabActive: {
      color: colors.teal600,
      borderBottomColor: colors.teal600
    },
    modalBody: {
      padding: '24px',
      flex: 1,
      overflowY: 'auto',
      overflowX: 'hidden'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      gap: '16px',
      marginBottom: '24px'
    },
    statCard: {
      background: colors.cream50,
      border: `1px solid rgba(94, 82, 64, 0.12)`,
      borderRadius: '8px',
      padding: '16px',
      textAlign: 'center'
    },
    statLabel: {
      fontSize: '12px',
      color: colors.slate500,
      marginBottom: '8px'
    },
    statValue: {
      fontSize: '24px',
      fontWeight: 550,
      color: colors.slate900
    },
    formRow: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '16px',
      marginBottom: '16px'
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      marginBottom: '16px'
    },
    formLabel: {
      fontSize: '12px',
      fontWeight: 500,
      color: colors.slate900,
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    },
    helperText: {
      fontSize: '11px',
      color: colors.slate500,
      marginTop: '4px',
      fontStyle: 'italic'
    },
    formInput: {
      padding: '10px 12px',
      fontSize: '14px',
      fontFamily: 'inherit',
      border: `1px solid rgba(94, 82, 64, 0.2)`,
      borderRadius: '8px',
      background: colors.cream50,
      color: colors.slate900,
      transition: 'all 0.2s ease',
      outline: 'none'
    },
    formInputDisabled: {
      padding: '10px 12px',
      fontSize: '14px',
      fontFamily: 'inherit',
      border: `1px solid rgba(94, 82, 64, 0.1)`,
      borderRadius: '8px',
      background: 'rgba(94, 82, 64, 0.05)',
      color: colors.slate500,
      cursor: 'not-allowed'
    },
    formTextarea: {
      padding: '10px 12px',
      fontSize: '14px',
      fontFamily: 'inherit',
      border: `1px solid rgba(94, 82, 64, 0.2)`,
      borderRadius: '8px',
      background: colors.cream50,
      color: colors.slate900,
      transition: 'all 0.2s ease',
      outline: 'none',
      resize: 'vertical',
      minHeight: '80px'
    },
    pricingSection: {
      background: 'rgba(33, 128, 141, 0.05)',
      border: `2px solid rgba(33, 128, 141, 0.2)`,
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '16px'
    },
    pricingSectionTitle: {
      fontSize: '14px',
      fontWeight: 550,
      color: colors.slate900,
      marginBottom: '12px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    lockedNotice: {
      background: 'rgba(255, 165, 0, 0.1)',
      border: `1px solid rgba(255, 165, 0, 0.3)`,
      borderRadius: '6px',
      padding: '10px 12px',
      fontSize: '12px',
      color: '#D97706',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginTop: '8px'
    },
    facilitiesGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '8px',
      marginTop: '8px'
    },
    facilityChip: {
      padding: '8px 12px',
      fontSize: '13px',
      border: `1px solid rgba(94, 82, 64, 0.2)`,
      borderRadius: '6px',
      background: colors.cream50,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      textAlign: 'center'
    },
    facilityChipActive: {
      background: colors.teal500,
      color: colors.cream50,
      borderColor: colors.teal500
    },
    equipmentGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '8px',
      marginTop: '8px'
    },
    equipmentChip: {
      padding: '8px 12px',
      fontSize: '13px',
      border: `1px solid rgba(94, 82, 64, 0.2)`,
      borderRadius: '6px',
      background: colors.cream50,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      textAlign: 'center'
    },
    equipmentChipActive: {
      background: colors.teal500,
      color: colors.cream50,
      borderColor: colors.teal500
    },
    bedListItem: {
      background: colors.cream50,
      border: `1px solid rgba(94, 82, 64, 0.12)`,
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '12px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    bedListInfo: {
      flex: 1
    },
    bedListActions: {
      display: 'flex',
      gap: '8px'
    },
    btnIcon: {
      padding: '8px',
      fontSize: '14px',
      fontFamily: 'inherit',
      border: `1px solid rgba(94, 82, 64, 0.2)`,
      borderRadius: '6px',
      background: colors.cream100,
      color: colors.slate900,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      transition: 'all 0.2s ease'
    },
    btnPrimary: {
      padding: '12px 24px',
      fontSize: '14px',
      fontFamily: 'inherit',
      fontWeight: 500,
      borderRadius: '8px',
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      background: colors.teal500,
      color: colors.cream50,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      width: '100%',
      marginTop: '8px'
    },
    btnDanger: {
      padding: '8px',
      fontSize: '14px',
      fontFamily: 'inherit',
      border: `1px solid ${colors.red500}`,
      borderRadius: '6px',
      background: 'rgba(192, 21, 47, 0.1)',
      color: colors.red500,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      transition: 'all 0.2s ease'
    },
    infoBox: {
      background: 'rgba(33, 128, 141, 0.08)',
      border: `1px solid rgba(33, 128, 141, 0.2)`,
      borderRadius: '8px',
      padding: '12px',
      fontSize: '13px',
      color: colors.slate700,
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '8px'
    },
    wardTypeCard: {
      background: colors.cream50,
      border: `1px solid rgba(94, 82, 64, 0.12)`,
      borderRadius: '8px',
      padding: '12px',
      marginBottom: '8px'
    }
  };

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>
            <WrenchIcon />
            {bedManagementMode === 'createWard' ? 'Create New Ward' : `Bed Management - ${currentWard?.name}`}
          </h2>
          <button 
            style={styles.closeButton}
            onClick={onClose}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(94, 82, 64, 0.12)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'none';
            }}
          >
            <XIcon />
          </button>
        </div>

        <div style={styles.tabContainer}>
          <button
            style={{
              ...styles.tab,
              ...(bedManagementMode === 'overview' ? styles.tabActive : {})
            }}
            onClick={() => setBedManagementMode('overview')}
          >
            Overview
          </button>
          <button
            style={{
              ...styles.tab,
              ...(bedManagementMode === 'add' ? styles.tabActive : {})
            }}
            onClick={() => setBedManagementMode('add')}
          >
            Add Bed
          </button>
          <button
            style={{
              ...styles.tab,
              ...(bedManagementMode === 'createWard' ? styles.tabActive : {})
            }}
            onClick={() => setBedManagementMode('createWard')}
          >
            Create Ward
          </button>
          <button
            style={{
              ...styles.tab,
              ...(bedManagementMode === 'maintenance' ? styles.tabActive : {})
            }}
            onClick={() => setBedManagementMode('maintenance')}
          >
            Maintenance
          </button>
        </div>

        <div style={styles.modalBody}>
          {bedManagementMode === 'overview' && (
            <>
              <div style={styles.statsGrid}>
                <div style={styles.statCard}>
                  <div style={styles.statLabel}>Total Beds</div>
                  <div style={styles.statValue}>{stats.total}</div>
                </div>
                <div style={styles.statCard}>
                  <div style={styles.statLabel}>Available</div>
                  <div style={{ ...styles.statValue, color: colors.teal500 }}>
                    {stats.available}
                  </div>
                </div>
                <div style={styles.statCard}>
                  <div style={styles.statLabel}>Occupied</div>
                  <div style={{ ...styles.statValue, color: colors.red500 }}>
                    {stats.occupied}
                  </div>
                </div>
                <div style={styles.statCard}>
                  <div style={styles.statLabel}>Maintenance</div>
                  <div style={{ ...styles.statValue, color: colors.gray400 }}>
                    {stats.maintenance}
                  </div>
                </div>
              </div>

              {/* Ward Pricing Info */}
              {currentWard?.dailyFare && (
                <div style={styles.pricingSection}>
                  <div style={styles.pricingSectionTitle}>
                    💰 Ward Pricing (Fixed)
                  </div>
                  <div style={styles.formRow}>
                    <div>
                      <div style={{ fontSize: '12px', color: colors.slate500, marginBottom: '4px' }}>
                        Daily Fare
                      </div>
                      <div style={{ fontSize: '20px', fontWeight: 550, color: colors.slate900 }}>
                        ₹{currentWard.dailyFare}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: colors.slate500, marginBottom: '4px' }}>
                        Deposit Amount
                      </div>
                      <div style={{ fontSize: '20px', fontWeight: 550, color: colors.slate900 }}>
                        ₹{currentWard.depositAmount}
                      </div>
                    </div>
                  </div>
                  <div style={styles.lockedNotice}>
                    🔒 Pricing is locked and applies to all beds in this ward
                  </div>
                </div>
              )}

              <h3 style={{ fontSize: '16px', fontWeight: 550, marginBottom: '16px' }}>
                All Beds in {currentWard?.name}
              </h3>

              {beds.length === 0 ? (
                <div style={styles.infoBox}>
                  <InfoIcon />
                  <span>No beds found in this ward. Click the "Add Bed" tab to create your first bed.</span>
                </div>
              ) : (
                beds.map(bed => (
                  <div key={bed.id} style={styles.bedListItem}>
                    <div style={styles.bedListInfo}>
                      <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                        {bed.id} - {bed.status}
                      </div>
                      <div style={{ fontSize: '13px', color: colors.slate500 }}>
                        {bed.wardType} | Floor {bed.floor}
                        {bed.patient && ` | Patient: ${bed.patient}`}
                        {currentWard?.dailyFare && ` | ₹${currentWard.dailyFare}/day`}
                      </div>
                    </div>
                    <div style={styles.bedListActions}>
                      <button
                        style={styles.btnIcon}
                        onClick={() => {
                          setSelectedBedForEdit(bed);
                          setEditBedData({
                            id: bed.id,
                            wardId: bed.wardId,
                            status: bed.status,
                            type: bed.type,
                            floor: bed.floor,
                            equipment: bed.equipment || [],
                            notes: bed.notes || '',
                            maintenanceSchedule: bed.maintenanceSchedule || ''
                          });
                          setShowEditBedModal(true);
                          onClose();
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(94, 82, 64, 0.12)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = colors.cream100;
                        }}
                      >
                        <EditIcon />
                      </button>
                      <button
                        style={styles.btnDanger}
                        onClick={() => handleRemoveBed(bed.id)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = colors.red500;
                          e.currentTarget.style.color = colors.cream50;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(192, 21, 47, 0.1)';
                          e.currentTarget.style.color = colors.red500;
                        }}
                      >
                        <TrashIcon />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </>
          )}

          {bedManagementMode === 'add' && (
            <>
              <div style={styles.infoBox}>
                <InfoIcon />
                <span>Add a bed to the ward. Pricing is inherited from ward settings and cannot be changed per bed.</span>
              </div>

              <form onSubmit={handleAddNewBed}>
                {/* Bed ID and Floor Row */}
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Bed ID *</label>
                    <input
                      type="text"
                      value={newBedData.id}
                      onChange={(e) => setNewBedData({ ...newBedData, id: e.target.value.toUpperCase() })}
                      style={styles.formInput}
                      required
                      placeholder="e.g., A21, ICU13, P9"
                      onFocus={(e) => e.currentTarget.style.borderColor = colors.teal500}
                      onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(94, 82, 64, 0.2)'}
                    />
                    <span style={styles.helperText}>Unique identifier for this bed</span>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Floor *</label>
                    <input
                      type="text"
                      value={newBedData.floor}
                      onChange={(e) => setNewBedData({ ...newBedData, floor: e.target.value })}
                      style={styles.formInput}
                      required
                      placeholder="e.g., 1, 2, Ground"
                      onFocus={(e) => e.currentTarget.style.borderColor = colors.teal500}
                      onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(94, 82, 64, 0.2)'}
                    />
                    <span style={styles.helperText}>Floor location of the bed</span>
                  </div>
                </div>

                {/* Bed Type */}
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Bed Type *</label>
                  <select
                    value={newBedData.type}
                    onChange={(e) => setNewBedData({ ...newBedData, type: e.target.value })}
                    style={styles.formInput}
                    onFocus={(e) => e.currentTarget.style.borderColor = colors.teal500}
                    onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(94, 82, 64, 0.2)'}
                  >
                    <option value="standard">Standard Bed</option>
                    <option value="electric">Electric Adjustable Bed</option>
                    <option value="icu_bed">ICU Bed with Monitoring</option>
                    <option value="bariatric">Bariatric Bed (Heavy Duty)</option>
                    <option value="pediatric">Pediatric Bed</option>
                    <option value="birthing">Birthing Bed</option>
                  </select>
                  <span style={styles.helperText}>Physical type of the bed</span>
                </div>

                {/* Equipment */}
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Available Equipment</label>
                  <div style={styles.equipmentGrid}>
                    {availableEquipment.map(equipment => (
                      <div
                        key={equipment}
                        style={{
                          ...styles.equipmentChip,
                          ...(newBedData.equipment?.includes(equipment) ? styles.equipmentChipActive : {})
                        }}
                        onClick={() => handleNewBedEquipmentToggle(equipment)}
                        onMouseEnter={(e) => {
                          if (!newBedData.equipment?.includes(equipment)) {
                            e.currentTarget.style.background = 'rgba(94, 82, 64, 0.08)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!newBedData.equipment?.includes(equipment)) {
                            e.currentTarget.style.background = colors.cream50;
                          }
                        }}
                      >
                        {equipment}
                      </div>
                    ))}
                  </div>
                  <span style={styles.helperText}>Click to select equipment available with this bed</span>
                </div>

                {/* Notes */}
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Additional Notes</label>
                  <textarea
                    value={newBedData.notes}
                    onChange={(e) => setNewBedData({ ...newBedData, notes: e.target.value })}
                    style={styles.formTextarea}
                    placeholder="Add any special notes or instructions for this bed..."
                    onFocus={(e) => e.currentTarget.style.borderColor = colors.teal500}
                    onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(94, 82, 64, 0.2)'}
                  />
                  <span style={styles.helperText}>Optional: Add any remarks or special instructions</span>
                </div>

                <button 
                  type="submit" 
                  style={styles.btnPrimary}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = colors.teal600;
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(33, 128, 141, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = colors.teal500;
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <PlusIcon />
                  Add New Bed
                </button>
              </form>
            </>
          )}

          {bedManagementMode === 'createWard' && (
            <>
              <div style={styles.infoBox}>
                <InfoIcon />
                <span>Create a new ward with pricing and facilities. Ward pricing cannot be modified after creation.</span>
              </div>

              <form onSubmit={handleCreateWard}>
                {/* Ward Name and Department */}
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Ward Name *</label>
                    <input
                      type="text"
                      value={newWardData?.name || ''}
                      onChange={(e) => setNewWardData({ ...newWardData, name: e.target.value })}
                      style={styles.formInput}
                      required
                      placeholder="e.g., Apollo Wing A"
                      onFocus={(e) => e.currentTarget.style.borderColor = colors.teal500}
                      onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(94, 82, 64, 0.2)'}
                    />
                    <span style={styles.helperText}>Unique name for this ward</span>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Department *</label>
                    <select
                      value={newWardData?.department || ''}
                      onChange={(e) => setNewWardData({ ...newWardData, department: e.target.value })}
                      style={styles.formInput}
                      required
                      onFocus={(e) => e.currentTarget.style.borderColor = colors.teal500}
                      onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(94, 82, 64, 0.2)'}
                    >
                      <option value="">Select Department</option>
                      {departmentOptions.map(dept => (
                        <option key={dept} value={dept}>{dept}</option>
                      ))}
                    </select>
                    <span style={styles.helperText}>Medical department for this ward</span>
                  </div>
                </div>

                {/* Ward Type */}
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Ward Type *</label>
                  <select
                    value={newWardData?.type || ''}
                    onChange={(e) => setNewWardData({ ...newWardData, type: e.target.value })}
                    style={styles.formInput}
                    required
                    onFocus={(e) => e.currentTarget.style.borderColor = colors.teal500}
                    onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(94, 82, 64, 0.2)'}
                  >
                    <option value="">Select Ward Type</option>
                    {wardTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label} - {type.description}
                      </option>
                    ))}
                  </select>
                  <span style={styles.helperText}>Category of accommodation provided</span>
                </div>

                {/* Floor and Capacity */}
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Floor/Location *</label>
                    <input
                      type="text"
                      value={newWardData?.floor || ''}
                      onChange={(e) => setNewWardData({ ...newWardData, floor: e.target.value })}
                      style={styles.formInput}
                      required
                      placeholder="e.g., 3rd Floor, Wing B"
                      onFocus={(e) => e.currentTarget.style.borderColor = colors.teal500}
                      onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(94, 82, 64, 0.2)'}
                    />
                    <span style={styles.helperText}>Physical location of the ward</span>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Total Bed Capacity *</label>
                    <input
                      type="number"
                      value={newWardData?.capacity || ''}
                      onChange={(e) => setNewWardData({ ...newWardData, capacity: e.target.value })}
                      style={styles.formInput}
                      required
                      placeholder="e.g., 25"
                      min="1"
                      max="50"
                      onFocus={(e) => e.currentTarget.style.borderColor = colors.teal500}
                      onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(94, 82, 64, 0.2)'}
                    />
                    <span style={styles.helperText}>Maximum beds (1-50 per ward)</span>
                  </div>
                </div>

                {/* Pricing Section - Non-editable after creation */}
                <div style={styles.pricingSection}>
                  <div style={styles.pricingSectionTitle}>
                     Ward Pricing (One-time Setup)
                  </div>
                  
                  <div style={styles.formRow}>
                    <div style={styles.formGroup}>
                      <label style={styles.formLabel}>Daily Fare (₹) *</label>
                      <input
                        type="number"
                        value={newWardData?.dailyFare || ''}
                        onChange={(e) => setNewWardData({ ...newWardData, dailyFare: e.target.value })}
                        style={styles.formInput}
                        required
                        placeholder="e.g., 3500"
                        min="0"
                        onFocus={(e) => e.currentTarget.style.borderColor = colors.teal500}
                        onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(94, 82, 64, 0.2)'}
                      />
                      <span style={styles.helperText}>Per day room rent for this ward</span>
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.formLabel}>Deposit Amount (₹) *</label>
                      <input
                        type="number"
                        value={newWardData?.depositAmount || ''}
                        onChange={(e) => setNewWardData({ ...newWardData, depositAmount: e.target.value })}
                        style={styles.formInput}
                        required
                        placeholder="e.g., 15000"
                        min="0"
                        onFocus={(e) => e.currentTarget.style.borderColor = colors.teal500}
                        onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(94, 82, 64, 0.2)'}
                      />
                      <span style={styles.helperText}>Initial deposit at admission</span>
                    </div>
                  </div>

                  <div style={styles.lockedNotice}>
                     Warning: Pricing cannot be changed after ward creation. All beds will inherit these rates.
                  </div>
                </div>

                {/* Ward Facilities */}
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Ward Facilities & Amenities</label>
                  <div style={styles.facilitiesGrid}>
                    {wardFacilities.map(facility => (
                      <div
                        key={facility}
                        style={{
                          ...styles.facilityChip,
                          ...(newWardData?.facilities?.includes(facility) ? styles.facilityChipActive : {})
                        }}
                        onClick={() => handleWardFacilityToggle(facility)}
                        onMouseEnter={(e) => {
                          if (!newWardData?.facilities?.includes(facility)) {
                            e.currentTarget.style.background = 'rgba(94, 82, 64, 0.08)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!newWardData?.facilities?.includes(facility)) {
                            e.currentTarget.style.background = colors.cream50;
                          }
                        }}
                      >
                        {facility}
                      </div>
                    ))}
                  </div>
                  <span style={styles.helperText}>Select amenities available in this ward</span>
                </div>

                {/* Description */}
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Ward Description</label>
                  <textarea
                    value={newWardData?.description || ''}
                    onChange={(e) => setNewWardData({ ...newWardData, description: e.target.value })}
                    style={styles.formTextarea}
                    placeholder="Describe the ward features, location, and any special notes..."
                    onFocus={(e) => e.currentTarget.style.borderColor = colors.teal500}
                    onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(94, 82, 64, 0.2)'}
                  />
                  <span style={styles.helperText}>Optional: Add detailed ward information</span>
                </div>

                <button 
                  type="submit" 
                  style={styles.btnPrimary}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = colors.teal600;
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(33, 128, 141, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = colors.teal500;
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <BuildingIcon />
                  Create Ward
                </button>
              </form>
            </>
          )}

          {bedManagementMode === 'maintenance' && (
            <>
              <div style={styles.infoBox}>
                <InfoIcon />
                <span>View and manage all beds currently under maintenance. Click edit to update status or schedule.</span>
              </div>

              <h3 style={{ fontSize: '16px', fontWeight: 550, marginBottom: '16px' }}>
                Beds Under Maintenance
              </h3>

              {beds.filter(b => b.status === 'maintenance').length === 0 ? (
                <p style={{ fontSize: '14px', color: colors.slate500, textAlign: 'center', padding: '32px' }}>
                  No beds currently under maintenance - all clear!
                </p>
              ) : (
                beds.filter(b => b.status === 'maintenance').map(bed => (
                  <div key={bed.id} style={styles.bedListItem}>
                    <div style={styles.bedListInfo}>
                      <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                        {bed.id}
                      </div>
                      <div style={{ fontSize: '13px', color: colors.slate500 }}>
                        {bed.notes || 'No maintenance notes'}
                        {bed.maintenanceSchedule && ` | Scheduled: ${bed.maintenanceSchedule}`}
                      </div>
                    </div>
                    <div style={styles.bedListActions}>
                      <button
                        style={styles.btnIcon}
                        onClick={() => {
                          setSelectedBedForEdit(bed);
                          setEditBedData({
                            id: bed.id,
                            wardId: bed.wardId,
                            status: bed.status,
                            type: bed.type,
                            floor: bed.floor,
                            equipment: bed.equipment || [],
                            notes: bed.notes || '',
                            maintenanceSchedule: bed.maintenanceSchedule || ''
                          });
                          setShowEditBedModal(true);
                          onClose();
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(94, 82, 64, 0.12)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = colors.cream100;
                        }}
                      >
                        <EditIcon />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BedManagementModal;
